import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Chip, Divider, Grid, Stack, Typography } from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { Link as RouterLink } from 'react-router-dom';
import { apiContractRequest } from '../../../api/client';
import OwnerNeuralQueue from './components/OwnerNeuralQueue';
import OwnerDebugExport from './components/OwnerDebugExport';

/**
 * WP-OWNER-PROTOCOL | PATCH 02 — FRONTEND MVP DE CONTROL
 * Autoridad: únicamente servicios Owner autenticados. NO CREA nuevos datos.
 * P01 backend: GET /api/owner/mvp.
 * Lecturas existentes: GET owner-group / neural-queue.
 * Mutaciones: solamente las acciones manuales existentes de OwnerNeuralQueue,
 * protegidas por confirmaciones y CAS del servidor.
 * No enviar mensajes desde esta consola: pruebas por WhatsApp real.
 */
const last = value => {
  if (!value) return 'Sin fecha';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? 'Sin fecha' : d.toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' });
};
const money = value => Number.isFinite(Number(value))
  ? Number(value).toLocaleString('es-AR', { maximumFractionDigits: 2 })
  : 'Sin datos';
const readable = (error, fallback) => error?.message || fallback;
const formatName = (entry) => (entry?.name || entry?.title || 'Sin nombre');

function Panel({ title, children, detail, action }) {
  return <Card variant="outlined" sx={{ borderRadius: 2.5, minWidth: 0, height: '100%' }}>
    <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>
      <Stack direction="row" gap={1} justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Box minWidth={0}><Typography variant="subtitle1" fontWeight={800}>{title}</Typography>
          {detail && <Typography variant="caption" color="text.secondary">{detail}</Typography>}</Box>
        {action}
      </Stack>
      {children}
    </CardContent>
  </Card>;
}
function Lines({ items, empty, render }) {
  if (!Array.isArray(items)) return <Alert severity="warning">Esta lectura no está disponible. No implica ausencia de registros.</Alert>;
  if (!items.length) return <Typography variant="body2" color="text.secondary">{empty}</Typography>;
  return <Stack spacing={0.6}>{items.map((item, index) => <Box key={item.id || index} sx={{ py: .55, borderBottom: '1px solid', borderColor: 'divider' }}>{render(item)}</Box>)}</Stack>;
}

export default function OwnerMvpPage() {
  const [snapshot, setSnapshot] = useState(null);
  const [group, setGroup] = useState(null);
  const [queue, setQueue] = useState(null);
  const [transport, setTransport] = useState(null);
  const [errors, setErrors] = useState({});
  const [refreshToken, setRefreshToken] = useState(0);
  const [updating, setUpdating] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    let active = true;
    let timeout = null;
    const controller = new AbortController();
    const load = async () => {
      if (!active) return;
      setUpdating(true);
      const endpoints = [
        ['/api/owner/mvp', { signal: controller.signal, retryAttempts: 0 }],
        ['/api/owner/control-center/owner-group', { signal: controller.signal, retryAttempts: 0 }],
        ['/api/owner/control-center/neural-queue?limit=60', { signal: controller.signal, retryAttempts: 0 }],
        ['/api/owner/whatsapp/baileys/status', { signal: controller.signal, retryAttempts: 0 }],
      ];
      const results = await Promise.allSettled(endpoints.map(([path, opts]) => apiContractRequest(path, opts)));
      if (!active) return;
      const nextErrors = {};
      const names = ['snapshot', 'group', 'queue', 'transport'];
      results.forEach((result, i) => {
        const name = names[i];
        if (result.status === 'rejected') { nextErrors[name] = readable(result.reason, 'No disponible'); return; }
        if (result.value?.ok === false) { nextErrors[name] = result.value.message || result.value.code || 'Respuesta no válida'; return; }
        if (name === 'snapshot') setSnapshot(result.value);
        if (name === 'group') setGroup(result.value);
        if (name === 'queue') setQueue(Array.isArray(result.value?.items) ? result.value.items : []);
        if (name === 'transport') setTransport(result.value);
      });
      setErrors(nextErrors);
      setUpdatedAt(new Date());
      setUpdating(false);
      timeout = setTimeout(load, 15000);
    };
    load();
    return () => { active = false; controller.abort(); if (timeout) clearTimeout(timeout); };
  }, [refreshToken]);

  const queueItems = useMemo(() => Array.isArray(queue) ? queue : [], [queue]);
  const jobsByInbound = useMemo(() => new Map((group?.jobs || []).map(job => [job.messageId, job])), [group]);
  const currentMessages = Array.isArray(group?.messages) ? group.messages : [];
  const queued = queueItems.filter(j => j.status === 'queued').length;
  const blocked = queueItems.filter(j => ['blocked', 'failed'].includes(j.status)).length;
  const reload = () => setRefreshToken(v => v + 1);
  const projects = snapshot?.projects;
  const finance = snapshot?.finance;
  const activity = snapshot?.day?.activeActivity;
  const activityStartedAt = activity?.startAt ? new Date(activity.startAt).getTime() : NaN;
  const activityHours = Number.isFinite(activityStartedAt) ? (Date.now() - activityStartedAt) / 3600000 : null;
  const staleActivity = activityHours !== null && activityHours > 16;


  return <Box sx={{ maxWidth: 1480, mx: 'auto', px: { xs: 1, md: 2 }, py: 1.5 }}>
    <Stack direction={{ xs:'column', sm:'row' }} justifyContent="space-between" alignItems={{ sm:'center' }} gap={1.5} sx={{ mb: 1.75 }}>
      <Box>
        <Typography variant="h5" fontWeight={900}>Owner · Mesa de control</Typography>
        <Typography variant="body2" color="text.secondary">Datos reales del Owner personal · pruebas desde el grupo de WhatsApp · sin nuevos registros</Typography>
        <Typography variant="caption" color="text.secondary">Última lectura: {updatedAt ? last(updatedAt) : 'Cargando...'} · automático cada 15 s</Typography>
      </Box>
      <Stack direction="row" gap={1} flexWrap="wrap">
        <Button size="small" variant="outlined" startIcon={<RefreshRoundedIcon />} disabled={updating} onClick={reload}>Actualizar</Button>
        <OwnerDebugExport />
      </Stack>
    </Stack>

    {Object.entries(errors).map(([key,value]) => <Alert severity="error" key={key} sx={{ mb: 1 }}>
      {key === 'snapshot' ? 'Resumen Owner' : key === 'group' ? 'Chat WhatsApp' : key === 'queue' ? 'Cola neuronal' : 'Estado WhatsApp'}: {value}.
      {key === 'snapshot' && ' Si es 404, falta aplicar las rutas del backend P01/P02.'}
      {' '}Se conservan en pantalla los últimos datos leídos, que podrían estar desactualizados.
    </Alert>)}
    {transport?.execution?.role === 'passive' && <Alert severity="info" sx={{mb:1.5}}>
      <strong>Este servidor es pasivo para WhatsApp Owner.</strong> No conecta ni desconecta el dispositivo y no procesa trabajos del chat.
      El estado «sin socket local» no significa que tu WhatsApp esté caído. El backend local debe ser la instancia activa.
    </Alert>}
    {transport?.execution?.role === 'active' && <Alert severity={transport?.baileys?.status === 'connected' ? 'success' : 'warning'} sx={{mb:1.5}}>
      <strong>Servidor autorizado para WhatsApp Owner.</strong> Estado local: {transport?.baileys?.status || 'desconocido'}.
      {transport?.baileys?.status !== 'connected' && ' Revisá la conexión real antes de hacer una prueba.'}
    </Alert>}
    {snapshot?.partial && <Alert severity="warning" sx={{ mb: 1 }}>Lectura parcial: {snapshot.unavailable?.join(', ') || 'verificar servicios'}. No se muestra una lista vacía como si fuera certeza.</Alert>}

    {staleActivity && <Alert severity="warning" sx={{mb:1.5}}>
      <strong>Actividad antigua sin cerrar:</strong> «{activity?.title}» desde {last(activity?.startAt)}.
      No se debe contabilizar automáticamente como trabajo. Por WhatsApp, pedile a Owner que
      te muestre el bloque pendiente y confirmá si debe descartarlo o corregirse.
    </Alert>}
    <Alert severity="info" sx={{mb:1.5}}>
      <strong>Tracking de hoy:</strong> escribile a Owner «Empiezo un bloque de reels para WebProfit»,
      después «Ahora paso a programación» y cuando termines «Cerrá el bloque».
      El registro válido es el que aparece en Mongo/Planning Brain, no sólo el texto del bot.
    </Alert>
    <Grid container spacing={1.3} sx={{ mb: 1.3 }}>
      <Grid item xs={6} md={3}><Panel title="Pendientes" detail="Abiertos · máximo 60 mostrados"><Typography variant="h4" fontWeight={900}>{snapshot?.commitments ? (snapshot.commitments.length === 60 ? '60+' : snapshot.commitments.length) : '—'}</Typography></Panel></Grid>
      <Grid item xs={6} md={3}><Panel title="Proyectos" detail="Iniciativas Owner · incluye CRM"><Typography variant="h4" fontWeight={900}>{projects?.total ?? '—'}</Typography></Panel></Grid>
      <Grid item xs={6} md={3}><Panel title="En cola" detail="Trabajos sin iniciar"><Typography variant="h4" fontWeight={900}>{queue === null ? '—' : queued}</Typography></Panel></Grid>
      <Grid item xs={6} md={3}><Panel title="Con fallas" detail="Jobs bloqueados o fallidos"><Typography variant="h4" fontWeight={900} color={blocked ? 'error.main' : 'text.primary'}>{queue === null ? '—' : blocked}</Typography></Panel></Grid>
    </Grid>

    <Grid container spacing={1.3} alignItems="stretch">
      <Grid item xs={12} lg={7}>
        <Panel title="WhatsApp · conversación" detail="Mensajes realmente registrados; no se envían mensajes desde esta pantalla" action={<Button size="small" variant="text" component={RouterLink} to="/dashboard/owner/centro-de-mando" endIcon={<OpenInNewRoundedIcon />}>Diagnóstico</Button>}>
          {group && !group.configured && <Alert severity="warning">No hay grupo Owner seleccionado para esta integración. Revisá la configuración de WhatsApp.</Alert>}
          <Box sx={{ maxHeight: 470, overflowY: 'auto', display:'flex', flexDirection:'column', gap:.75 }}>
            {currentMessages.map(msg => {
              const assistant = /^\s*🤖\s*Owner\s*:/i.test(String(msg.text || ''));
              const label = assistant ? 'Owner' : msg.direction === 'outbound' ? 'Vos' : 'Participante';
              const job = jobsByInbound.get(msg.id);
              return <Box key={msg.id} sx={{ border: '1px solid', borderColor: 'divider', p:1.1, borderRadius:2, bgcolor: assistant ? 'action.hover' : 'background.paper', maxWidth:'98%' }}>
                <Stack direction="row" gap={1} alignItems="center" justifyContent="space-between"><Typography variant="caption" fontWeight={800}>{label}</Typography><Typography variant="caption" color="text.secondary">{last(msg.at)}</Typography></Stack>
                <Typography variant="body2" sx={{ whiteSpace:'pre-wrap', overflowWrap:'anywhere', mt:.35 }}>{msg.text || '(mensaje sin texto)'}</Typography>
                {job && <Chip sx={{mt:.6}} size="small" variant="outlined" label={`IA: ${job.status}${job.phase ? ` · ${job.phase}` : ''}`} color={['failed','blocked'].includes(job.status)?'error':job.status==='completed'?'success':'default'} />}
              </Box>;
            })}
            {group?.configured && currentMessages.length===0 && <Typography color="text.secondary" variant="body2">Sin mensajes recientes en el ledger. Esto no demuestra que WhatsApp esté desconectado.</Typography>}
            {!group && <Typography color="text.secondary" variant="body2">{updating ? 'Cargando chat...' : 'Chat sin datos disponibles.'}</Typography>}
          </Box>
        </Panel>
      </Grid>
      <Grid item xs={12} lg={5}>
        <Panel title="Hoy" detail="Lectura del Planning Brain existente" action={<Button size="small" component={RouterLink} to="/dashboard/owner" variant="text">Abrir Hoy</Button>}>
          {snapshot?.day ? <Stack spacing={.8}>
            <Typography variant="body2"><strong>Próxima acción:</strong> {snapshot.day.nextRecommendedAction?.title || 'Sin sugerencia verificada'}</Typography>
            <Typography variant="body2"><strong>Próximo evento:</strong> {snapshot.day.nextEvent?.title || 'Sin evento confirmado'} {snapshot.day.nextEvent?.startAt ? `· ${last(snapshot.day.nextEvent.startAt)}` : ''}</Typography>
            <Typography variant="body2"><strong>Actividad:</strong> {snapshot.day.activeActivity?.title || 'Sin actividad activa'} {snapshot.day.activeActivity?.startAt ? `· desde ${last(snapshot.day.activeActivity.startAt)}` : ''}</Typography>
            <Typography variant="body2"><strong>Minutos registrados:</strong> {snapshot.day.trackedMinutes ?? 'No disponible'}</Typography>
          </Stack> : <Alert severity="warning">No se pudo verificar el resumen del día.</Alert>}
          <Divider sx={{my:1.3}} />
          <Typography variant="subtitle2" fontWeight={800} sx={{mb:.7}}>Pendientes ({snapshot?.commitments?.length ?? '—'})</Typography>
          <Box sx={{ maxHeight:205, overflowY:'auto' }}>
            <Lines items={snapshot?.commitments} empty="Sin compromisos abiertos en esta lectura." render={item => <>
              <Typography variant="body2" fontWeight={700}>{item.title}</Typography>
              <Typography variant="caption" color="text.secondary">{item.state || 'ready'} · {item.dueAt ? last(item.dueAt) : 'Sin fecha'} · {item.priority || 'Normal'}</Typography>
            </>} />
          </Box>
        </Panel>
      </Grid>
      <Grid item xs={12} md={6}>
        <Panel title="Dinero" detail="Libro principal Owner · saldos existentes" action={<Button component={RouterLink} to="/dashboard/owner/finanzas" size="small">Ver finanzas</Button>}>
          {!finance ? <Alert severity="warning">Resumen financiero no disponible.</Alert> : <>
            <Typography variant="body2" sx={{mb:.75}}>Cuentas: {finance.accountCount ?? '—'}</Typography>
            {Object.keys(finance.totalsByCurrency || {}).map(currency => <Typography key={currency} variant="body2"><strong>Saldo {currency}:</strong> {money(finance.totalsByCurrency[currency])}</Typography>)}
            {Object.keys(finance.monthByCurrency || {}).map(currency => <Typography key={`month-${currency}`} variant="caption" display="block" color="text.secondary">Mes {currency}: ingresos {money(finance.monthByCurrency[currency]?.income)} · gastos {money(finance.monthByCurrency[currency]?.expense)}</Typography>)}
          </>}
          <Alert severity="info" sx={{mt:1}}>
            <strong>Prueba de finanzas por WhatsApp:</strong> consultá tus cuentas reales; después registrá un gasto pequeño existente.
            Owner debe mostrar un <strong>borrador sin impacto</strong> y esperar tu confirmación en otro mensaje.
            Solo el resultado registrado en MongoDB modifica el saldo. No pruebes todavía préstamos ni recurrencias.
          </Alert>
        </Panel>
      </Grid>
      <Grid item xs={12} md={6}>
        <Panel title="Proyectos y vida" detail="Registros Owner; todavía sin depurar comerciales" action={<Stack direction="row" gap={1}><Button size="small" component={RouterLink} to="/dashboard/owner/vida">Vida</Button><Button size="small" component={RouterLink} to="/dashboard/owner/proyectos">Proyectos</Button></Stack>}>
          {!projects ? <Alert severity="warning">Proyectos no disponibles.</Alert> : <Box sx={{maxHeight:160,overflowY:'auto'}}><Lines items={projects.items} empty="Sin iniciativas Owner activas." render={p => <Typography variant="body2">{formatName(p)} <Typography component="span" variant="caption" color="text.secondary">· {p.status || 'sin estado'}</Typography></Typography>} /></Box>}
          <Divider sx={{my:1}} />
          <Typography variant="body2" fontWeight={700}>Sueño: {snapshot?.life?.lastSleep ? `${last(snapshot.life.lastSleep.startedAt)} → ${snapshot.life.lastSleep.endedAt ? last(snapshot.life.lastSleep.endedAt) : 'en curso'}` : 'Sin sesión verificable'}</Typography>
          <Typography variant="caption" color="text.secondary">Entradas personales recientes: {snapshot?.life?.recent?.length ?? '—'}</Typography>
        </Panel>
      </Grid>
      <Grid item xs={12}>
        <Panel title="Cola neuronal" detail="Estados reales; cancelar o reintentar sólo bajo confirmación">
          <Alert severity="info" sx={{mb:1}}>Un job «completed» confirma procesamiento del worker, no que el mensaje se haya entregado al teléfono.</Alert>
          <OwnerNeuralQueue items={queueItems} loading={updating} error={errors.queue || ''} onRefresh={reload} />
        </Panel>
      </Grid>
    </Grid>
  </Box>;
}
